import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2 } from "lucide-react";
import { categories } from "../data/products";
import { VG_ORANGE } from "../lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  "Customer Service",
  "Sample Request",
  "Sales Support",
  "General Questions",
];

export const ContactForm = ({
  department,
  defaultReason = "",
  showProductInterest = false,
  showShipping = false,
  showProjectTitle = false,
  requireProjectTitle = false,
  formType = "contact",
  productName = "",
  colorLabel = "",
  prefillMessage = "",
  sampleColors = [],
  initialSelectedColors = [],
  compact = false,
  testId = "contact-form",
}) => {
  const hasSampleSelector = sampleColors.length > 0;
  const empty = {
    name: "",
    company: "",
    email: "",
    phone: "",
    reason: defaultReason,
    message: prefillMessage,
    product_interest: colorLabel ? `${productName} — ${colorLabel}` : productName || "",
    shipping_address: "",
    project_title: "",
    specifiers_location: "",
    representative: "",
    customer_type: "",
    hp_field: "",
  };
  const [form, setForm] = useState(empty);
  const [sampleType, setSampleType] = useState("individual"); // "individual" | "chainset"
  const [sampleSize, setSampleSize] = useState("3x5"); // "3x5" | "9x9"
  const [selectedColors, setSelectedColors] = useState(initialSelectedColors);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const startTime = useRef(Date.now());

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleColor = (name) =>
    setSelectedColors((prev) =>
      prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]
    );

  const buildSampleDetails = () => {
    if (!hasSampleSelector) return "";
    if (sampleType === "chainset") {
      return `Full chain set — all ${sampleColors.length} colors as 3"x5" swatches`;
    }
    const size = sampleSize === "9x9" ? '9"x9"' : '3"x5"';
    return `${size} swatches — ${selectedColors.join(", ")}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.phone) {
      toast.error("Please complete all required fields.");
      return;
    }
    if (hasSampleSelector && sampleType === "individual" && selectedColors.length === 0) {
      toast.error("Please select at least one color to sample.");
      return;
    }
    if (!hasSampleSelector && !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    if (showProjectTitle && requireProjectTitle && !form.project_title) {
      toast.error("Please enter the project title / architect.");
      return;
    }
    if (showShipping && !form.shipping_address) {
      toast.error("Please enter a shipping address for your samples.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        department,
        form_type: formType,
        ...form,
        sample_details: buildSampleDetails(),
        elapsed_ms: Date.now() - startTime.current,
      });
      toast.success("Your message has been sent. Our team will be in touch shortly.");
      setDone(true);
      setForm(empty);
      setSelectedColors(initialSelectedColors);
      setSampleType("individual");
      setSampleSize("3x5");
    } catch (err) {
      const msg =
        err?.response?.status === 429
          ? "Too many submissions. Please try again in a few minutes."
          : "Something went wrong. Please try again or call us directly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={`${testId}-success`}
        className="rounded-lg border border-green-200 bg-green-50 p-8 text-center"
      >
        <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
        <h3 className="mt-4 font-heading text-xl font-bold text-neutral-900">Thank you!</h3>
        <p className="mt-2 text-sm text-neutral-600">
          Your inquiry has been received. A member of our team will contact you soon.
        </p>
        <Button
          variant="outline"
          className="mt-6"
          data-testid={`${testId}-another`}
          onClick={() => setDone(false)}
        >
          Send another message
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={testId} className="space-y-4">
      {/* Honeypot: hidden from humans, bots tend to fill it */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.hp_field}
            onChange={(e) => update("hp_field", e.target.value)}
          />
        </label>
      </div>
      <div className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <div className="space-y-1.5">
          <Label htmlFor={`${testId}-name`}>Name *</Label>
          <Input
            id={`${testId}-name`}
            data-testid={`${testId}-name`}
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Your full name"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${testId}-company`}>Company *</Label>
          <Input
            id={`${testId}-company`}
            data-testid={`${testId}-company`}
            value={form.company}
            onChange={(e) => update("company", e.target.value)}
            placeholder="Company name"
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${testId}-email`}>Email *</Label>
          <Input
            id={`${testId}-email`}
            data-testid={`${testId}-email`}
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@company.com"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${testId}-phone`}>Phone *</Label>
          <Input
            id={`${testId}-phone`}
            data-testid={`${testId}-phone`}
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="(000) 000-0000"
          />
        </div>
      </div>

      {productName && (
        <div
          className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-neutral-700"
          data-testid={`${testId}-product-name`}
        >
          Product: <span className="font-semibold text-neutral-900">{productName}</span>
          {colorLabel && (
            <> — Color: <span className="font-semibold text-neutral-900">{colorLabel}</span></>
          )}
        </div>
      )}

      {hasSampleSelector && (
        <div className="space-y-4 rounded-lg border border-neutral-200 p-4" data-testid={`${testId}-sample-selector`}>
          <div className="space-y-2">
            <Label>Sample type *</Label>
            <RadioGroup
              className="flex flex-col gap-2 sm:flex-row sm:gap-6"
              value={sampleType}
              onValueChange={setSampleType}
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="individual" id={`${testId}-type-individual`} data-testid={`${testId}-type-individual`} />
                <Label htmlFor={`${testId}-type-individual`} className="font-normal cursor-pointer">Individual swatches</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="chainset" id={`${testId}-type-chainset`} data-testid={`${testId}-type-chainset`} />
                <Label htmlFor={`${testId}-type-chainset`} className="font-normal cursor-pointer">
                  Full chain set <span className="text-neutral-400">(all colors, 3&quot;&times;5&quot;)</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {sampleType === "individual" ? (
            <>
              <div className="space-y-2">
                <Label>Choose color(s) *</Label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid={`${testId}-color-list`}>
                  {sampleColors.map((c) => {
                    const checked = selectedColors.includes(c);
                    return (
                      <label
                        key={c}
                        data-testid={`${testId}-color-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                          checked ? "border-[#ED8B00] bg-orange-50 text-neutral-900" : "border-neutral-200 hover:border-neutral-300"
                        }`}
                      >
                        <Checkbox checked={checked} onCheckedChange={() => toggleColor(c)} />
                        <span>{c}</span>
                      </label>
                    );
                  })}
                </div>
                {selectedColors.length > 0 && (
                  <p className="text-xs text-neutral-500" data-testid={`${testId}-color-count`}>
                    {selectedColors.length} color{selectedColors.length > 1 ? "s" : ""} selected
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Sample size *</Label>
                <RadioGroup
                  className="flex gap-6"
                  value={sampleSize}
                  onValueChange={setSampleSize}
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="3x5" id={`${testId}-size-3x5`} data-testid={`${testId}-size-3x5`} />
                    <Label htmlFor={`${testId}-size-3x5`} className="font-normal cursor-pointer">3&quot; &times; 5&quot;</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="9x9" id={`${testId}-size-9x9`} data-testid={`${testId}-size-9x9`} />
                    <Label htmlFor={`${testId}-size-9x9`} className="font-normal cursor-pointer">9&quot; &times; 9&quot;</Label>
                  </div>
                </RadioGroup>
              </div>
            </>
          ) : (
            <p className="rounded-md bg-neutral-50 px-3 py-2 text-sm text-neutral-600" data-testid={`${testId}-chainset-note`}>
              We&apos;ll send a full chain set of all {sampleColors.length} colors as 3&quot;&times;5&quot; swatches on a ring.
            </p>
          )}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Reason for Contact</Label>
          <Select value={form.reason} onValueChange={(v) => update("reason", v)}>
            <SelectTrigger data-testid={`${testId}-reason`}>
              <SelectValue placeholder="Select a reason" />
            </SelectTrigger>
            <SelectContent>
              {REASONS.map((r) => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {showProductInterest && (
          <div className="space-y-1.5">
            <Label>Product Interest</Label>
            <Select value={form.product_interest} onValueChange={(v) => update("product_interest", v)}>
              <SelectTrigger data-testid={`${testId}-product-interest`}>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {showProjectTitle && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`${testId}-project-title`}>Project Title / Architect {requireProjectTitle ? "*" : ""}</Label>
            <Input
              id={`${testId}-project-title`}
              data-testid={`${testId}-project-title`}
              value={form.project_title}
              onChange={(e) => update("project_title", e.target.value)}
              placeholder="Project name and/or architect"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${testId}-specifiers-location`}>Specifiers' Location</Label>
            <Input
              id={`${testId}-specifiers-location`}
              data-testid={`${testId}-specifiers-location`}
              value={form.specifiers_location}
              onChange={(e) => update("specifiers_location", e.target.value)}
              placeholder="City, State"
            />
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <Label>I am a</Label>
        <RadioGroup
          className="flex gap-6"
          value={form.customer_type}
          onValueChange={(v) => update("customer_type", v)}
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="new" id={`${testId}-new`} data-testid={`${testId}-new-customer`} />
            <Label htmlFor={`${testId}-new`} className="font-normal cursor-pointer">New Customer</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="existing" id={`${testId}-existing`} data-testid={`${testId}-existing-customer`} />
            <Label htmlFor={`${testId}-existing`} className="font-normal cursor-pointer">Existing Customer</Label>
          </div>
        </RadioGroup>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${testId}-representative`}>Who is your Representative?</Label>
        <Input
          id={`${testId}-representative`}
          data-testid={`${testId}-representative`}
          value={form.representative}
          onChange={(e) => update("representative", e.target.value)}
          placeholder="Your van Gelder, Inc. representative (if known)"
        />
      </div>

      {showShipping && (
        <div className="space-y-1.5">
          <Label htmlFor={`${testId}-shipping`}>Shipping Address *</Label>
          <Textarea
            id={`${testId}-shipping`}
            data-testid={`${testId}-shipping`}
            rows={3}
            value={form.shipping_address}
            onChange={(e) => update("shipping_address", e.target.value)}
            placeholder="Street address, city, state, ZIP"
          />
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor={`${testId}-message`}>
          {hasSampleSelector ? "Additional notes" : "Message *"}
        </Label>
        <Textarea
          id={`${testId}-message`}
          data-testid={`${testId}-message`}
          rows={hasSampleSelector ? 3 : 5}
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder={hasSampleSelector ? "Anything else we should know? (optional)" : "How can we help you?"}
        />
      </div>

      <Button
        type="submit"
        disabled={submitting}
        data-testid={`${testId}-submit`}
        className="w-full text-white hover:opacity-90"
        style={{ backgroundColor: VG_ORANGE }}
      >
        {submitting ? "Sending..." : "Send Message"}
      </Button>
    </form>
  );
};
